import { useState, useEffect, useCallback, useRef } from "react";
import API, { formatError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Image as ImageIcon, Upload, Trash2, Loader2, Plus, Save, Check } from "lucide-react";

// Pre-defined keys so admins cannot create arbitrary slots (keeps UI consistent with code lookup)
const ASSET_CATEGORIES = {
  segment: {
    label: "Segment Logos",
    hint: "Shown under Products → Segment selection. SVG/PNG recommended, square, transparent background.",
    items: [
      { key: "segment_clinical_chemistry", name: "Clinical Chemistry" },
      { key: "segment_hematology", name: "Hematology" },
      { key: "segment_critical_care", name: "Critical Care" },
      { key: "segment_urinalysis", name: "Urinalysis" },
      { key: "segment_hemostasis", name: "Hemostasis" },
      { key: "segment_esr", name: "ESR" },
      { key: "segment_diabetes_management", name: "Diabetes Management" },
      { key: "segment_immunology", name: "Immunology" },
      { key: "segment_molecular", name: "Molecular" },
    ],
  },
  icon: {
    label: "UI Icons",
    hint: "Used across tabs and category cards.",
    items: [
      { key: "icon_instrument", name: "Instrument Tab Icon" },
      { key: "icon_reagent", name: "Reagent Tab Icon" },
    ],
  },
  new_launch: {
    label: "New Launch Hero Images",
    hint: "Up to 4 featured cards on customer dashboard New Launch section. Ideal 1:1 ratio, 800×800 px.",
    items: [
      { key: "new_launch_1", name: "New Launch — Slot 1" },
      { key: "new_launch_2", name: "New Launch — Slot 2" },
      { key: "new_launch_3", name: "New Launch — Slot 3" },
      { key: "new_launch_4", name: "New Launch — Slot 4" },
    ],
  },
};

export default function AdminAssets() {
  const [assets, setAssets] = useState({});
  const [editName, setEditName] = useState({}); // key -> draft name
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");
  const [savedKey, setSavedKey] = useState("");
  const [error, setError] = useState("");
  const fileInputs = useRef({});
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/admin/assets");
      const map = {};
      (data || []).forEach(a => { map[a.key] = a; });
      setAssets(map);
    } catch (err) { setError(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (key, category, name, file) => {
    if (!file) return;
    setSavingKey(key); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await API.post("/admin/assets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await API.post("/admin/assets", {
        key, category, name, image_url: up.data.url, is_active: true, sort_order: 0,
      });
      setSavedKey(key);
      setTimeout(() => setSavedKey(""), 2500);
      load();
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setSavingKey("");
    }
  };

  const handleRemove = async (key) => {
    if (!window.confirm("Remove this image?")) return;
    try { await API.delete(`/admin/assets/${encodeURIComponent(key)}`); load(); }
    catch (err) { setError(formatError(err.response?.data?.detail)); }
  };

  const saveName = async (key, category, defaultName) => {
    const newName = (editName[key] ?? "").trim() || defaultName;
    setSavingKey(key); setError("");
    try {
      const existing = assets[key] || {};
      await API.post("/admin/assets", {
        key, category, name: newName,
        image_url: existing.image_url || "",
        sort_order: existing.sort_order || 0,
        is_active: existing.is_active !== false,
      });
      setSavedKey(key);
      setTimeout(() => setSavedKey(""), 2000);
      load();
    } catch (err) { setError(formatError(err.response?.data?.detail) || "Failed to save"); }
    finally { setSavingKey(""); }
  };

  const previewUrl = (asset) => {
    if (!asset?.image_url) return "";
    return asset.image_url.startsWith("http") ? asset.image_url : `${BACKEND}${asset.image_url}`;
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#008A99]" /></div>;

  return (
    <div className="space-y-8" data-testid="admin-assets">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: "Calibri, Carlito, 'Segoe UI', sans-serif" }}>Brand Assets</h2>
        <p className="text-sm text-gray-500 mt-0.5">Upload and manage segment logos, category icons and promotional hero images.</p>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm" data-testid="assets-error">{error}</div>}

      {Object.entries(ASSET_CATEGORIES).map(([catKey, cat]) => (
        <section key={catKey} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid={`assets-section-${catKey}`}>
          <div className="mb-4">
            <h3 className="font-semibold text-[#0F172A] flex items-center gap-2" style={{ fontFamily: "Calibri, Carlito, 'Segoe UI', sans-serif" }}>
              <ImageIcon className="w-5 h-5 text-[#008A99]" /> {cat.label}
            </h3>
            <p className="text-xs text-gray-500 mt-1">{cat.hint}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {cat.items.map(item => {
              const asset = assets[item.key];
              const url = previewUrl(asset);
              const isSaving = savingKey === item.key;
              const justSaved = savedKey === item.key;
              return (
                <div key={item.key} className="border border-slate-200 rounded-xl p-3 flex flex-col gap-2" data-testid={`asset-card-${item.key}`}>
                  <div className="aspect-square w-full bg-slate-50 rounded-lg overflow-hidden flex items-center justify-center border border-slate-100">
                    {url ? (
                      <img src={url} alt={item.name} className="w-full h-full object-contain" data-testid={`asset-preview-${item.key}`} />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-slate-300" />
                    )}
                  </div>
                  <p className="text-xs font-semibold text-slate-700 truncate">{asset?.name || item.name}</p>
                  <p className="text-[10px] text-slate-400 font-mono truncate">{item.key}</p>
                  {catKey === "new_launch" && (
                    <div className="flex items-center gap-1.5">
                      <input
                        type="text"
                        defaultValue={asset?.name || item.name}
                        onChange={(e) => setEditName(prev => ({ ...prev, [item.key]: e.target.value }))}
                        placeholder="Product name"
                        className="flex-1 h-7 text-[11px] px-2 rounded-lg border border-slate-200 focus:outline-none focus:border-[#008A99]"
                        data-testid={`asset-name-input-${item.key}`}
                      />
                      <button
                        onClick={() => saveName(item.key, catKey, item.name)}
                        disabled={isSaving}
                        className="px-2 h-7 text-[10px] font-semibold rounded-lg bg-slate-100 hover:bg-[#008A99] hover:text-white disabled:opacity-60"
                        data-testid={`asset-name-save-${item.key}`}
                      >
                        {savedKey === item.key ? <Check className="w-3 h-3" /> : <Save className="w-3 h-3" />}
                      </button>
                    </div>
                  )}
                  <input
                    ref={(el) => { fileInputs.current[item.key] = el; }}
                    type="file" accept="image/*,.svg" hidden
                    onChange={(e) => handleUpload(item.key, catKey, item.name, e.target.files?.[0])}
                    data-testid={`asset-file-${item.key}`}
                  />
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => fileInputs.current[item.key]?.click()}
                      disabled={isSaving}
                      className="flex-1 flex items-center justify-center gap-1.5 h-8 text-xs font-semibold rounded-lg bg-[#008A99] hover:bg-[#006670] text-white disabled:opacity-60"
                      data-testid={`asset-upload-btn-${item.key}`}
                    >
                      {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : justSaved ? <Check className="w-3.5 h-3.5" />
                        : url ? <><Upload className="w-3.5 h-3.5" /> Replace</>
                        : <><Upload className="w-3.5 h-3.5" /> Upload</>}
                    </button>
                    {url && (
                      <button onClick={() => handleRemove(item.key)} className="p-2 rounded-lg text-red-500 hover:bg-red-50" data-testid={`asset-remove-${item.key}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
